import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { UIOption } from './ui-option';
import type {
    Attributes,
    ElementChild,
    ElementConfig,
} from '../ui-element.type';
import styles from './ui-option-search.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIOption}
 * @alias UIOptionSearch
 * @element ui-option-search
 * @classdesc Represents a class for <code>ui-option-search</code> element.
 * Similar to ui-option, but holds a search result with optional area label
 * and formatted (bold) text. Used with combination with ui-autocomplete only.
 * @property {string} [area] {@attr area} Defines an area of the search result.
 * @property {string} [formatted] {@attr formatted} Formatted version of the label with
 * bold segments delimited by double asterisks.
 * @example
 * <ui-option-search></ui-option-search>
 */

@Component({
    tag: 'ui-option-search',
    styles,
})
export class UIOptionSearch extends UIOption {
    @Attribute(String) area?: string;
    @Attribute(String) formatted?: string;

    /**
     * Finds match in option value and given texts.
     * @param {HTMLOptionElement} option
     * @param {string} text
     * @returns {boolean}
     */
    findMatch(option: HTMLOptionElement, text: string): boolean {
        return (
            option.getAttribute('value')?.match(new RegExp(text, 'i')) !== null
        );
    }

    /**
     * Returns own dataset as attributes.
     * @returns {Attributes}
     */
    getData(): Attributes {
        return this.getAttributes();
    }

    /**
     * Builds the text/label fragment, optionally with bold segments and area label.
     * @private
     * @returns {ElementConfig}
     */
    private renderTextValue(): ElementConfig {
        const baseTextValue =
            this.getAttribute('text') || this.getAttribute('value') || '';
        const children: Array<ElementChild> = [];

        if (this.formatted && baseTextValue !== this.formatted) {
            children.push(
                ...this.formatted
                    .split('**')
                    .map((chunk, index): ElementChild => {
                        if (index % 2 === 1) {
                            return { tagName: 'strong', children: [chunk] };
                        }
                        return chunk;
                    })
            );
        } else {
            children.push(baseTextValue);
        }

        if (!this.area || this.control) {
            return { tagName: 'span', children };
        }

        return {
            tagName: 'div',
            classList: { 'ui-option-search__group': true },
            children: [
                { tagName: 'span', children },
                {
                    tagName: 'span',
                    classList: { 'ui-option-search__area': true },
                    children: [this.area],
                },
            ],
        };
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.insertElements([this.renderTextValue()]);
        super.render();
    }
}
